const MAILERLITE_SCRIPT_SRC = 'https://assets.mailerlite.com/js/universal.js';

/** Test account id. Prefer NEXT_PUBLIC_MAILERLITE_ACCOUNT_ID; this is the final fallback. */
export const MAILERLITE_ACCOUNT_ID = process.env.NEXT_PUBLIC_MAILERLITE_ACCOUNT_ID ?? '2232454';

/** Skip redundant reinit calls fired close together (e.g. route hook + embed mount). */
const REINIT_DEDUPE_MS = 400;
let lastReinitAt = 0;

type MlFn = (...args: unknown[]) => unknown;

const getMl = (): MlFn | undefined => {
  if (typeof window === 'undefined') return undefined;
  return (window as Window & { ml?: MlFn }).ml;
};

/** Pull the account id out of a pasted MailerLite snippet, e.g. `ml('account', '2232454')`. */
export const extractMailerLiteAccountId = (html: string): string | undefined =>
  html.match(/ml\(\s*['"]account['"]\s*,\s*['"]([^'"]+)['"]/)?.[1];

/** Read the account id from the live MailerLite instance (`ml('account')` returns it). */
export const getLiveMailerLiteAccountId = (): string | undefined => {
  const ml = getMl();
  if (typeof ml !== 'function') return undefined;
  try {
    const id = ml('account');
    return typeof id === 'string' && id ? id : undefined;
  } catch {
    return undefined;
  }
};

/** True when the page currently contains a MailerLite embedded-form container. */
export const hasMailerLiteEmbed = (root: ParentNode = document): boolean =>
  typeof document !== 'undefined' && root.querySelector('.ml-embedded[data-form]') !== null;

/** True when MailerLite has been loaded in this session (popups or embeds in use). */
export const isMailerLiteLoaded = (): boolean => typeof getMl() === 'function';

/**
 * Fully reset MailerLite's global context and load it again.
 *
 * Re-loading `universal.js` alone is a no-op once `window.ml` exists — the snippet guards
 * with `w.ml = w.ml || …` and the script skips re-scanning the DOM. So on Next.js client
 * navigations newly mounted `.ml-embedded` containers never render. Deleting the context
 * first forces a clean re-init (embeds re-scan, popup page-rules re-evaluate) like a hard
 * refresh would.
 *
 * The account id is resolved from (in order): the explicit arg, the live instance, env.
 */
export const reinitMailerLite = (accountId?: string): void => {
  if (typeof document === 'undefined') return;

  const now = Date.now();
  if (now - lastReinitAt < REINIT_DEDUPE_MS) return;

  const resolvedId = accountId ?? getLiveMailerLiteAccountId() ?? MAILERLITE_ACCOUNT_ID;
  if (!resolvedId) return;

  lastReinitAt = now;

  // Teardown — scoped to popups + universal.js ONLY. Inline embedded forms (`.embedForm`,
  // rendered by CustomHtmlContent with their own webforms.min.js) are self-contained and must
  // survive navigation, so we never touch `[id^="mlb2-"]` broadly or `groot.mailerlite.com`.
  delete (window as Window & { ml?: MlFn }).ml;

  document
    .querySelectorAll('script[src*="assets.mailerlite.com/js/universal.js"]')
    .forEach(node => node.remove());

  // Auto-embed containers (the `data-form` variant) re-render on the fresh scan.
  document.querySelectorAll('.ml-embedded').forEach(el => {
    el.innerHTML = '';
  });

  // Remove only popup wrappers/overlays — not inline embed forms (`.embedForm`).
  document.querySelectorAll('.ml-form-embedWrapper.embedPopup').forEach(wrapper => {
    (wrapper.closest('.ml-form-embedContainer') ?? wrapper).remove();
  });
  document.querySelectorAll('.ml-form-overlay').forEach(node => node.remove());

  // Re-init: a fresh script (cache-busted) defines a clean `ml` and re-scans the DOM.
  const script = document.createElement('script');
  script.async = true;
  script.src = `${MAILERLITE_SCRIPT_SRC}?_=${now}`;
  script.onload = () => getMl()?.('account', resolvedId);
  document.body.appendChild(script);
};
